import { WarningOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import { useDataIssues } from '../context';
import { IssueUser } from '../types';
import { RISK_LABEL, atName } from '../utils';
import styles from '../style.module.scss';

/** Chữ phụ cho ô chưa có giá trị ("Chưa tìm lại được", "Chưa có tài khoản"...). */
export const Muted = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.muted}>{children}</span>
);

/** Dòng chữ nhỏ dưới giá trị chính của một ô. */
export const Sub = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.sub}>{children}</div>
);

interface TiktokIdProps {
  username?: string | null;
  /** Dòng phụ dưới TikTok ID (vd. cột "Trạng thái" nguyên văn của file). */
  sub?: string | null;
}

export const TiktokIdCell = ({ username, sub }: TiktokIdProps) => {
  const name = atName(username);
  return (
    <div className={styles.wrapAnywhere}>
      {name ? <span className={styles.tiktokId}>{name}</span> : <Muted>(trống)</Muted>}
      {sub && <Sub>{sub}</Sub>}
    </div>
  );
};

interface UidProps {
  uid?: string | number | null;
  /** Chữ hiện khi không có UID. */
  empty?: string;
}

/** UID luôn đi kèm nút chép. */
export const UidCell = ({ uid, empty = '—' }: UidProps) => {
  const text = uid === null || uid === undefined ? '' : String(uid).trim();
  if (!text) return <Muted>{empty}</Muted>;
  return (
    <Typography.Text
      className={styles.uid}
      copyable={{ text, tooltips: ['Chép UID', 'Đã chép'] }}
    >
      {text}
    </Typography.Text>
  );
};

interface AccountProps {
  user?: IssueUser | null;
  /** Tên đăng nhập trong app khi máy chủ không gửi kèm `user`. */
  username?: string | null;
  /** Chữ khi không có tài khoản (mặc định "Chưa có tài khoản"). */
  empty?: string;
}

/**
 * Tài khoản app: tên + @tên đăng nhập. ADMIN bấm được để sang trang Tài khoản (trang đó chỉ
 * ADMIN vào được); MOD chỉ thấy chữ.
 */
export const AccountCell = ({ user, username, empty = 'Chưa có tài khoản' }: AccountProps) => {
  const { isAdmin } = useDataIssues();
  const login = atName(user?.username || username);
  const name = user?.nickName?.trim() || '';
  if (!user && !login) return <Muted>{empty}</Muted>;

  const primary = name || login || `#${user?.id}`;
  const body = (
    <>
      <span className={styles.personName}>{primary}</span>
      {name && login && <span className={styles.personSub}>{login}</span>}
    </>
  );
  if (!isAdmin) return <span className={styles.person}>{body}</span>;
  return (
    <Link
      to="/users-management"
      className={`${styles.person} ${styles.personLink}`}
      title="Mở trang Tài khoản"
    >
      {body}
    </Link>
  );
};

/** Nhãn rủi ro của tab "Vắng khỏi file": biểu tượng + chữ, không chỉ bằng màu. */
export const RiskLabel = () => (
  <span className={styles.risk}>
    <WarningOutlined aria-hidden />
    <span>{RISK_LABEL}</span>
  </span>
);
